import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { CtaButton } from '../../shared-components/CtaButton/CtaButton';
import { Input } from '../../shared-components/Input/Input';
import { PageHeader } from '../../shared-components/PageHeader/PageHeader';

import styles from './ServerConfig.module.css';
import { actions, thunk, useAppDispatch, useAppSelector } from 'redux-stores';

export default function ServerConfig() {
  const dispatch = useAppDispatch();
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedOrganizationId = useAppSelector((state) => state.admin.selectedOrganizationId);

  const {
    standinBaseUrl,
    standinWifiSsids,
    isLocalServer,
    isStandinEnabled,
    standinTargetToken,
    isBackupLoading,
    isDirty,
    isLoading,
  } = useAppSelector((state) => state.serverConfig);

  useEffect(() => {
    thunk.fetchServerConfig(dispatch);
    thunk.fetchBackupRegistration(dispatch);
  }, [dispatch, selectedOrganizationId]);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  const onSave = useCallback(() => thunk.saveServerConfig(dispatch), [dispatch]);

  const onCancel = useCallback(() => thunk.fetchServerConfig(dispatch), [dispatch]);

  const handleCopyToken = useCallback(async () => {
    if (!standinTargetToken) return;
    try {
      await navigator.clipboard.writeText(standinTargetToken);
      setCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[ServerConfig] Failed to copy standin token', err);
    }
  }, [standinTargetToken]);

  const handleStandinToggle = useCallback(() => {
    if (isStandinEnabled) {
      thunk.disableStandinBackup(dispatch);
    } else {
      thunk.enableStandinBackup(dispatch);
    }
  }, [dispatch, isStandinEnabled]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <PageHeader label="Server Configuration" />
        </header>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <PageHeader label="Server Configuration" />
          <span
            className={classNames(styles.statusPill, {
              [styles.statusPillActive]: isLocalServer,
              [styles.statusPillInactive]: !isLocalServer,
            })}
          >
            <span
              className={classNames(styles.statusDot, {
                [styles.statusDotActive]: isLocalServer,
                [styles.statusDotInactive]: !isLocalServer,
              })}
            />
            {isLocalServer ? 'Standalone Active' : 'Inactive'}
          </span>
        </div>
        {!isLocalServer && (
          <div className={styles.actionButtons}>
            <CtaButton label="Cancel" dataTestId="serverConfigCancel" onClick={onCancel} />
            <CtaButton
              primary
              label="Save Settings"
              dataTestId="serverConfigSave"
              onClick={onSave}
              disabled={!isDirty}
            />
          </div>
        )}
      </header>

      <div className={styles.content}>
        {isLocalServer && (
          <div className={styles.readOnlyBanner} data-test-id="serverConfigReadOnlyBanner">
            <span className={styles.readOnlyIcon}>i</span>
            <div>
              <p className={styles.readOnlyTitle}>Read-Only Mode</p>
              <p className={styles.readOnlyText}>
                This page is read-only on the standin server. Configuration is managed from the
                cloud.
              </p>
            </div>
          </div>
        )}

        <div className={styles.section}>
          <h3 className={styles.sectionHeader}>Network</h3>
          <div className={styles.settingRow}>
            <div className={styles.settingLabel}>
              <p className={styles.settingLabelText}>Base URL</p>
              <p className={styles.settingLabelSub}>
                The URL clients use to connect to this standalone server.
              </p>
            </div>
            <div className={styles.settingControl}>
              <Input
                value={standinBaseUrl}
                placeholder="https://standin.example.com"
                dataTestId="serverConfigBaseUrlInput"
                disabled={isLocalServer}
                onInputChange={(e) =>
                  dispatch(actions.updateServerConfig({ standinBaseUrl: e.target.value }))
                }
              />
            </div>
          </div>
          <div className={styles.settingRow}>
            <div className={styles.settingLabel}>
              <p className={styles.settingLabelText}>WiFi SSIDs</p>
              <p className={styles.settingLabelSub}>
                Comma-separated list of WiFi networks that indicate the client is on the local
                network.
              </p>
            </div>
            <div className={styles.settingControl}>
              <Input
                value={standinWifiSsids}
                placeholder="HospitalNet, HospitalNet-5G"
                dataTestId="serverConfigWifiSsidsInput"
                disabled={isLocalServer}
                onInputChange={(e) =>
                  dispatch(actions.updateServerConfig({ standinWifiSsids: e.target.value }))
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionHeader}>Standin Backup</h3>
          <div className={styles.settingRow}>
            <div className={styles.settingLabel}>
              <p className={styles.settingLabelText}>Enable Standin Server</p>
              <p className={styles.settingLabelSub}>
                When enabled, this organization's data will be backed up for standin server restore.
              </p>
            </div>
            <div className={styles.settingControl}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={isStandinEnabled}
                  onChange={handleStandinToggle}
                  disabled={isBackupLoading || isLocalServer}
                  data-test-id="serverConfigStandinToggle"
                />
                Enable standin backup for this organization
              </label>
            </div>
          </div>
          {isStandinEnabled && (
            <div className={styles.settingRow}>
              <div className={styles.settingLabel}>
                <p className={styles.settingLabelText}>Standin Token</p>
                <p className={styles.settingLabelSub}>
                  Auto-generated token identifying this org on the standin server. Use this in the
                  ECS task definition.
                </p>
              </div>
              <div className={styles.settingControl}>
                <div className={styles.copyField}>
                  <div className={styles.copyFieldInput}>
                    <Input
                      value={standinTargetToken}
                      dataTestId="serverConfigStandinTokenInput"
                      disabled
                    />
                  </div>
                  <button
                    type="button"
                    className={classNames(styles.copyButton, {
                      [styles.copyButtonSuccess]: copied,
                    })}
                    onClick={handleCopyToken}
                    data-test-id="serverConfigCopyStandinToken"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
